import { config } from '@/lib/config'

/**
 * Backend posługuje się kodem ISO 639-1 dla ukraińskiego (`uk`), a frontend historycznie
 * używa `ua` (config.availableLanguages, nazwy pól formularzy `nameUa`, klucze i18n).
 * Tłumaczymy wyłącznie na granicy sieci — modele i formularze FE zostają na `ua`.
 */
const FE_TO_BE_LANG: Record<string, string> = { ua: 'uk' }
const BE_TO_FE_LANG: Record<string, string> = { uk: 'ua' }

/** FE → BE. Używać tylko przy budowaniu payloadu wychodzącego. */
export const toBackendLang = (lang: string): string => FE_TO_BE_LANG[lang] ?? lang

/** BE → FE. Używać tylko przy mapowaniu odpowiedzi. Przepuszcza `ua` bez zmian (wsteczna zgodność). */
export const toFrontendLang = (lang: string): string => BE_TO_FE_LANG[lang] ?? lang

export const mappedLanguagesToCountryCodes = {
	pl: 'pl',
	en: 'gb',
	ua: 'ua',
}

export const getDefaultTranslation = (names: any): string => {
	if (typeof names === 'string') return names
	if (!Array.isArray(names)) return ''
	const name = names.find(n => n.lang === config.defaultLanguage)
	return name ? name.content : (names[0]?.content ?? '')
}

export const generateLangContent = (value: string, lang: string = config.defaultLanguage): ILangContent => ({
	lang: toBackendLang(lang ?? config.defaultLanguage),
	content: value,
})

export const generateLangContentArray = (value: string, lang: string = config.defaultLanguage): ILangContent[] => [
	generateLangContent(value, lang),
]

export const generateLangContentArrayFromObject = (obj: Record<string, string>): ILangContent[] =>
	Object.entries(obj).map(([lang, content]) => generateLangContent(content, lang))

export const LangContentToModel = (json: ILangContent[] | null | undefined): ILangContentModel => {
	if (!Array.isArray(json) || json.length === 0) return { pl: '' }
	// @ts-ignore Model to have a better mapping of lang to content. PL is required.
	return json.map(lg => ({ [toFrontendLang(lg.lang)]: lg.content })).reduce((acc, curr) => ({ ...acc, ...curr }), {})
}

export const LangContentToJson = (
	model: ILangContentModel,
	updated?: string,
	languages: string[] = [config.defaultLanguage],
): ILangContent[] =>
	Object.entries(model).map(([lang, content]) => {
		// `languages` operuje na kodach FE, więc porównujemy przed tłumaczeniem.
		if (!languages.includes(lang)) return { lang: toBackendLang(lang), content }

		return { lang: toBackendLang(lang), content: updated ?? content }
	})

export const LangContentToCreatePayload = (model: {
	name: string
	nameEn?: string | null
	nameUa?: string | null
}): ILangContent[] => {
	const names = {
		pl: model.name,
		en: model.nameEn,
		ua: model.nameUa,
	}

	return generateLangContentArrayFromObject(names as Record<string, string>)
}

export const LangContentToUpdatePayload = (
	model: {
		name: string
		nameEn?: string | null
		nameUa?: string | null
	},
	updated?: {
		name: ILangContentModel
	} | null,
): ILangContent[] => {
	const names = {
		pl: model.name ?? updated?.name.pl,
		en: model.nameEn ?? updated?.name.en,
		ua: model.nameUa ?? updated?.name.ua,
	}

	return generateLangContentArrayFromObject(names as Record<string, string>)
}

export const safeLangContentMapper = (value: ILangContent[] | null | undefined): ILangContentModel =>
	Array.isArray(value) ? LangContentToModel(value) : { pl: '' }
